import { multiplyMoney } from '@shared/lib/money';
import { BaggageAdapter, SelectedUpgrade } from './types';
import { Baggage, SelectedBaggageItem } from '../../BaggageService/types';
import { Passenger, Segment } from '../types';
import { BaggageType } from '@websky/graphql';
import { getStringsHasChanges } from '../../utils';

interface GetEmptyServicesItemsParams {
	baggage: Baggage[];
	segmentId?: string;
	passengerId: string;
}

/**
 * Builds a stable, order-independent cache key for a baggage service.
 * Deduplicates and sorts segment IDs so that ["SEG1","SEG2"] and ["SEG2","SEG1"]
 * map to the same key.
 *
 * Pure function — safe to call multiple times with the same inputs.
 */
const getBaggageServiceKey = (rfisc: string, segmentIds: string[]): string => {
	return `${rfisc}/${[...new Set(segmentIds)].sort().join('/')}`;
};

/**
 * Walks the baggage list and returns the single "winning" Baggage per
 * (rfics, segmentIdGroup) key — the one with the highest numeric ID.
 * Bags that are ineligible for this passenger/segment are excluded.
 */
export const buildActualBaggageMap = ({
	baggage,
	segmentId,
	passengerId
}: GetEmptyServicesItemsParams): Map<string, Baggage> => {
	const actualBaggageMap = new Map<string, Baggage>();

	for (const bag of baggage) {
		if (!bag.canBeAdded || !bag.allowedPassengers.includes(passengerId)) {
			continue;
		}

		for (const segmentIdGroup of bag.segmentIds) {
			if (segmentId && !segmentIdGroup.includes(segmentId)) {
				continue;
			}

			const key = getBaggageServiceKey(bag.rfics, segmentIdGroup);
			const existBaggage = actualBaggageMap.get(key);

			if (!existBaggage || Number(existBaggage.id) < Number(bag.id)) {
				actualBaggageMap.set(key, bag);
			}
		}
	}

	return actualBaggageMap;
};

/**
 * Produces the full list of SelectedUpgrade items for every (bag, segmentIdGroup)
 * combination. Items are marked `disabledToAdd` when the bag is not the
 * authoritative ("actual") service for that key.
 */
export const getEmptyServicesItems = ({
	baggage,
	segmentId,
	passengerId
}: GetEmptyServicesItemsParams): SelectedUpgrade[] => {
	const actualBaggageMap = buildActualBaggageMap({ baggage, segmentId, passengerId });

	const items: SelectedUpgrade[] = [];

	for (const bag of baggage) {
		for (const segmentIdGroup of bag.segmentIds) {
			const key = getBaggageServiceKey(bag.rfics, segmentIdGroup);
			const actualService = actualBaggageMap.get(key);

			items.push({
				passengerId,
				count: 0,
				segmentIds: segmentIdGroup,
				serviceId: bag.id,
				disabledToAdd: actualService?.id !== bag.id
			});
		}
	}

	return items;
};

export const detectDifferentForReturnFlight = (segments: Segment[], selectedServices: SelectedUpgrade[]): boolean => {
	const servicesCount = new Map<string, number>(),
		segmentsOfServices = new Map<string, string[]>();

	for (const selectedService of selectedServices) {
		const serviceId = selectedService.serviceId,
			passengerId = selectedService.passengerId,
			key = `${passengerId}:${serviceId}`;

		if (!servicesCount.has(key)) {
			servicesCount.set(key, selectedService.count);
		}

		// detect case if different service count
		if (servicesCount.get(key) !== selectedService.count) {
			return true;
		}

		// collect all segments where service is active
		segmentsOfServices.set(key, [...(segmentsOfServices.get(key) || []), ...selectedService.segmentIds]);
	}

	for (const segmentOfService of segmentsOfServices.values()) {
		if (new Set(segmentOfService).size !== segments.length) {
			return true;
		}
	}

	return false;
};

export const joinServicesByRfics = (baggage: Baggage[]): Baggage[][] => {
	const servicesByRficsMap = new Map<string, Baggage[]>();

	baggage.forEach(baggage => {
		if (servicesByRficsMap.has(baggage.rfics)) {
			servicesByRficsMap.get(baggage.rfics).push(baggage);
		} else {
			servicesByRficsMap.set(baggage.rfics, [baggage]);
		}
	});

	return [...servicesByRficsMap.values()];
};

export const joinIncludedBaggageByRfics = (includedServices: SelectedBaggageItem[]): SelectedBaggageItem[][] => {
	const includedServiceMap = new Map<string, SelectedBaggageItem[]>();

	includedServices.forEach(service => {
		const key = `${service.confirmedCount}_${service.baggage?.rfics}`;

		if (includedServiceMap.has(key)) {
			includedServiceMap.get(key).push(service);
		} else {
			includedServiceMap.set(key, [service]);
		}
	});

	const includedBaggageByRfics: SelectedBaggageItem[][] = [];

	includedServiceMap.forEach(items => {
		includedBaggageByRfics.push(items);
	});

	return includedBaggageByRfics;
};

export const filterIncludedBaggageByRfics = (
	baggageServices: Baggage[],
	includedBaggage: SelectedBaggageItem[],
	selectedBaggage: SelectedUpgrade[]
): SelectedBaggageItem[] => {
	const baggageServiceMap = new Map(baggageServices.map(service => [service.id, service]));

	const selectedBaggageKeySet = new Set<string>();

	selectedBaggage.forEach(baggage => {
		baggage.segmentIds.forEach(segmentId => {
			const gdsService = baggageServiceMap.get(baggage.serviceId);

			if (gdsService) {
				selectedBaggageKeySet.add(`${gdsService.rfics}_${baggage.passengerId}_${segmentId}`);
			}
		});
	});

	const filteredIncludedServices: SelectedBaggageItem[] = [];

	includedBaggage.forEach(baggage => {
		if (baggage.baggage) {
			baggage.segmentIds.forEach(segmentId => {
				const isReplaced = baggage.baggage.isReplacedBy?.some(rfics =>
					selectedBaggageKeySet.has(`${rfics}_${baggage.passengerId}_${segmentId}`)
				);

				if (!isReplaced) {
					filteredIncludedServices.push(baggage);
				}
			});
		}
	});

	return filteredIncludedServices;
};

export const getRelatedSegments = (groups: string[][], segmentId: string = null): Set<string> => {
	const result = new Set<string>();

	if (segmentId === null) {
		groups.forEach(group => {
			group.forEach(segmentId => result.add(segmentId));
		});
	} else {
		groups.some(group => {
			if (group.includes(segmentId)) {
				group.forEach(segmentId => result.add(segmentId));

				return true;
			}

			return false;
		});
	}

	return result;
};

export const getActualPriceInBaggageGroup = (baggages: Baggage[], multipleSegments?: boolean) => {
	let price = {
		amount: baggages[0].price.amount,
		currency: baggages[0].price.currency,
		withFrom: false
	};

	let actualBaggage: Baggage = null;
	baggages.forEach(bag => {
		const lessActualPrice = !actualBaggage || bag.price.amount < actualBaggage?.price.amount;

		if (isActualBaggage(bag, actualBaggage) && lessActualPrice) {
			actualBaggage = bag;
		}

		if (bag.price.amount < actualBaggage?.price.amount || bag.price.amount > actualBaggage?.price.amount) {
			price.withFrom = true;
		}
	});

	if (actualBaggage) {
		const segmentsCount = multipleSegments ? getRelatedSegments(actualBaggage.segmentIds).size : 1;

		price = {
			...price,
			amount: multiplyMoney(actualBaggage.price, segmentsCount).amount
		};
	}

	return price;
};

export const getSegmentsInBaggageGroup = (baggages: Baggage[]) => {
	const segmentsSet = new Set<string>();

	baggages.forEach(baggage =>
		baggage.segmentIds.forEach(segments => segments.forEach(segment => segmentsSet.add(segment)))
	);

	return segmentsSet;
};

export const getMinBaggageCount = (passenger: Passenger, baggage: Baggage[], segmentId?: string) => {
	const baggageIdSet = new Set(baggage.map(bag => bag.id));

	return (
		passenger.services?.reduce((total, service) => {
			const isIncludeServiceId = baggageIdSet.has(service.serviceId);
			const isIncludeSegment = !segmentId || (segmentId && service.segmentIds.includes(segmentId));
			if (!isIncludeServiceId || !isIncludeSegment) {
				return total;
			}

			return total + (service.confirmedCount ?? 0);
		}, 0) ?? 0
	);
};

export const isActualBaggage = (currBaggage: BaggageAdapter, actualBaggage: BaggageAdapter, onlyAvailable = true) => {
	const actualBaggageById = !actualBaggage || +currBaggage.id > +actualBaggage.id;

	if (onlyAvailable) {
		return currBaggage.canBeAdded && actualBaggageById;
	}

	return actualBaggageById;
};

export const getActualBaggage = (baggage: Baggage[], segmentId?: string, onlyAvailable = true): Baggage => {
	let actualBaggage: Baggage = null;

	baggage.forEach(bag => {
		const segmentIdSet = new Set(bag.segmentIds.reduce((ids, segmentIds) => [...ids, ...segmentIds], []));
		const isIncludeSegment = !segmentId || (segmentId && segmentIdSet.has(segmentId));

		if (isActualBaggage(bag, actualBaggage, onlyAvailable) && isIncludeSegment) {
			actualBaggage = bag;
		}
	});

	return actualBaggage;
};

export const updateSelectedItems = (
	currCount: number,
	newCount: number,
	items: SelectedUpgrade[], // empty items we can fill by current counter
	selectedItems: SelectedUpgrade[] // items previously selected by user for current segment, rfics and passenger
) => {
	const dCount = newCount - currCount;

	// count > 1 can be booked by multiple services with same ids
	// Join services with same segment ids
	const selectedServicesBySegments = new Map<string, SelectedUpgrade[]>();
	selectedItems.forEach(selected => {
		const key = selected.segmentIds.sort().join('-');

		selectedServicesBySegments.set(key, [...(selectedServicesBySegments.get(key) || []), selected]);
	});

	if (dCount > 0) {
		items.forEach(item => {
			if (!item.disabledToAdd) {
				const key = item.segmentIds.sort().join('-');

				const existServicesBySegment = selectedServicesBySegments.get(key) ?? [];
				const existServiceById = existServicesBySegment.find(service => service.serviceId === item.serviceId);

				if (existServiceById) {
					selectedServicesBySegments.set(
						key,
						existServicesBySegment.map(service => {
							if (service.serviceId === item.serviceId) {
								return { ...service, count: service.count + dCount };
							}

							return service;
						})
					);
				} else {
					const existServicesCount = existServicesBySegment.reduce(
						(acc, service) => +acc + +service.count,
						0
					);

					selectedServicesBySegments.set(key, [
						...existServicesBySegment,
						{ ...item, count: newCount - existServicesCount }
					]);
				}
			}
		});
	} else {
		// Change the count only first service in segment and reset the rest
		selectedServicesBySegments.forEach((services, key) => {
			const servicesWithCount = services.filter(service => service.count > 0);

			selectedServicesBySegments.set(
				key,
				servicesWithCount.map((service, index) => {
					const isLastService = index === servicesWithCount.length - 1;

					if (isLastService) {
						return { ...service, count: service.count + dCount };
					}

					return service;
				})
			);
		});
	}

	return [...selectedServicesBySegments.values()].reduce((acc, services) => [...acc, ...services], []);
};

export const isBaggageExcess = (baggage: Baggage) => {
	return baggage.type === BaggageType.BaggageExcess;
};

export const isCanModifyServices = (services: Baggage[], passenger: Passenger) => {
	return services.some(baggage => {
		if (!baggage.allowedPassengers.includes(passenger.id)) {
			return false;
		}

		return baggage.canBeAdded;
	});
};

export const getSelectedServicesKeys = (services: SelectedUpgrade[]) => {
	const selectedServicesSet = new Set<string>();

	services.forEach(service => {
		if (service.count) {
			const key = [service.serviceId, service.passengerId, service.segmentIds.join(''), service.count].join('-');

			selectedServicesSet.add(key);
		}
	});

	return [...selectedServicesSet].sort();
};

export const getBaggageHasChanges = (initialServices: SelectedUpgrade[], selectedServices: SelectedUpgrade[]) => {
	const initialKeys = getSelectedServicesKeys(initialServices);
	const selectedKeys = getSelectedServicesKeys(selectedServices);

	return getStringsHasChanges(initialKeys, selectedKeys);
};

export const getSelectedServicesWithConfirmedCount = (
	selectedServices: SelectedUpgrade[],
	passengers: Passenger[]
): SelectedUpgrade[] => {
	return selectedServices.map(selected => {
		const confirmedCount =
			passengers
				.find(passenger => passenger.id === selected.passengerId)
				?.services?.find(
					service =>
						service.serviceId === selected.serviceId && service.segmentIds[0] === selected.segmentIds[0]
				)?.confirmedCount ?? 0;

		return {
			...selected,
			confirmedCount
		};
	});
};
